import type { Workspace } from '../../types/workspace'
import type { StockItem } from '../../types/stock'
import type { Quota } from '../../types/quota'
import type { ResourceReadiness, ResourceSignal } from '../../types/resource'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import {
  buildResourceSignals,
  normalizeStockRequirements,
  quotaWeightedContribution,
} from '../../services/resourceAdapter'
import type { HelperOpenBlocker } from '../../types/helperBlocker'
import { formatHelperDueLabel } from './helperSchedule'

export type HelperReadinessModel = {
  status: ResourceReadiness['status'] | 'blocked'
  signals: ResourceSignal[]
  dueLabel: string | null
  requirementLabels: string[]
  dependencyReasons: string[]
  quotaContribution: number | null
  openBlockers: HelperOpenBlocker[]
  stockShortagesById: HelperOpenBlocker['shortagesById']
  stockItemIds: string[]
  needsStock: boolean
  hasAnySignal: boolean
}

export const buildHelperReadinessModel = (input: {
  item: HelperAssignedItem
  workspace: Workspace | null | undefined
  stockItems?: StockItem[]
  quotas?: Quota[]
  openBlockers?: HelperOpenBlocker[]
  nowMs?: number
  locale?: string
}): HelperReadinessModel => {
  const stockItems = input.stockItems ?? []
  const stockById = new Map(stockItems.map((item) => [item.id, item]))
  const stockRequirements = normalizeStockRequirements(input.item)
  const stockShortagesById = Object.fromEntries(
    stockRequirements.flatMap((requirement) => {
      const stock = stockById.get(requirement.stockItemId)
      const available = stock ? Math.max(0, stock.quantity - stock.reservedQuantity) : 0
      if (available >= requirement.quantity) return []
      return [[requirement.stockItemId, {
        name: stock?.name,
        available,
        required: requirement.quantity,
        unit: requirement.unitOverride ?? stock?.unit ?? '',
      }]]
    }),
  )
  const resource = buildResourceSignals({
    workspace: input.workspace,
    node: input.item,
    stockItems,
    quotas: input.quotas ?? [],
  })
  const openBlockers = input.openBlockers ?? []
  const dependencyReasons = input.item.dependencyBlockReasons ?? []
  const status: HelperReadinessModel['status'] =
    input.item.dependencyBlocked || openBlockers.length > 0
      ? 'blocked'
      : resource.status
  const activeWeightedQuota = (input.quotas ?? []).some((quota) => quota.targets.weighted_progress)
  const quotaContribution = activeWeightedQuota ? quotaWeightedContribution(input.item) : null
  const requirementLabels = [
    ...(input.item.requiresProofFile ? ['Proof'] : []),
    ...(input.item.requiresLocationPin ? ['Location'] : []),
    ...(input.item.requiresCoSign ? ['Co-sign'] : []),
  ]
  const needsStock = Object.keys(stockShortagesById).length > 0

  return {
    status,
    signals: resource.signals,
    dueLabel: formatHelperDueLabel(input.item, input.nowMs, input.locale),
    requirementLabels,
    dependencyReasons,
    quotaContribution,
    openBlockers,
    stockShortagesById,
    stockItemIds: stockRequirements.map((requirement) => requirement.stockItemId),
    needsStock,
    hasAnySignal: Boolean(
      resource.signals.length
      || requirementLabels.length
      || dependencyReasons.length
      || openBlockers.length
      || input.item.dependencyBlocked
      || input.item.scheduleEndDate
      || input.item.scheduleStartDate
    ),
  }
}
